import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  CategoryIcon,
  ChatIcon,
  GraphIcon,
  UserSquareIcon,
  SearchFavoriteIcon,
  RankingIcon,
  MoneyIcon,
  CalculatorIcon,
  TaskSquareIcon,
  ChartIcon,
  BookIcon,
  CardTickIcon,
  SettingIcon,
  FeedbackIcon,
  GlobeIcon,
  AuditIcon,
  MethodologiesIcon,
  ToolsIcon,
} from '@/components/icons/MenuIcons';
import { useAuth, useSubscription } from '@/utils/AuthContext';
import { BUILDER_ENABLED, METHODOLOGIES_ENABLED, SITE_AUDIT_ENABLED, TOOLS_ENABLED } from '@/utils/config';
import { useLanguage } from '@/i18n/LanguageContext';
import LocalizedLink from '@/components/shared/LocalizedLink';
import LanguageSwitcher from '@/components/shared/LanguageSwitcher';

const navItems = [
  { icon: CategoryIcon, labelKey: 'sidebar.home', href: '/dashboard' },
  { icon: ChatIcon, labelKey: 'sidebar.chat', href: '/chat' },
  { icon: GraphIcon, labelKey: 'sidebar.marketAnalysis', href: '/market-analysis' },
  { icon: UserSquareIcon, labelKey: 'sidebar.persona', href: '/personas' },
  { icon: SearchFavoriteIcon, labelKey: 'sidebar.research', href: '/research' },
  { icon: RankingIcon, labelKey: 'sidebar.positioning', href: '/positioning' },
  { icon: MoneyIcon, labelKey: 'sidebar.businessModel', href: '/business-model' },
  { icon: CalculatorIcon, labelKey: 'sidebar.financialModel', href: '/financial-model' },
  { icon: ChartIcon, labelKey: 'sidebar.marketingStrategy', href: '/marketing-strategy' },
  { icon: TaskSquareIcon, labelKey: 'sidebar.mvpPlan', href: '/mvp-plan' },
  ...(METHODOLOGIES_ENABLED ? [{ icon: MethodologiesIcon, labelKey: 'sidebar.methodologies', href: '/methods' }] : []),
  ...(BUILDER_ENABLED ? [{ icon: GlobeIcon, labelKey: 'sidebar.builder', href: '/builder' }] : []),
  ...(SITE_AUDIT_ENABLED ? [{ icon: AuditIcon, labelKey: 'sidebar.siteAudit', href: '/site-audit' }] : []),
  ...(TOOLS_ENABLED ? [{ icon: ToolsIcon, labelKey: 'sidebar.tools', href: '/tools' }] : []),
];

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  onOpenFeedback?: () => void;
}

export default function Sidebar({ isOpen, onClose, onOpenFeedback }: SidebarProps) {
  const location = useLocation();
  const { user } = useAuth();
  const { tier } = useSubscription();
  const { t } = useTranslation('common');
  const { lang, stripLangPrefix } = useLanguage();

  const userName = user?.name || user?.email?.split('@')[0] || t('user.defaultName');
  const currentPath = stripLangPrefix(location.pathname);

  const tierLabels: Record<string, string> = {
    free: '',
    student: t('tier.student'),
    basic: lang === 'en' ? 'Basic' : 'Базовый',
    full: lang === 'en' ? 'Full' : 'Полный',
    creator: t('tier.creator'),
    top_creator: t('tier.topCreator'),
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={onClose}
        />
      )}
      <aside className={cn(
        "flex h-dvh w-[240px] flex-col overflow-y-auto overscroll-contain border-r bg-[var(--grey-background,#f8f8f8)]",
        "fixed inset-y-0 left-0 z-40 transition-transform duration-300 md:relative md:translate-x-0",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}>
      {/* Logo */}
      <div className="flex items-center gap-[8px] px-[24px] py-[24px]">
        <LocalizedLink to="/dashboard" className="flex items-center gap-[8px]">
          <img src={lang === 'en' ? '/logo-en.svg' : '/logo.png'} alt={t('sidebar.logoAlt')} className="h-5" />
          <span className="inline-flex items-center justify-center border border-[var(--grey-accent,#eeeeee)] rounded-[8px] px-[8px] py-[4px] text-[10px] font-medium text-[color:var(--grey-text,#565858)] font-manrope">
            Beta
          </span>
        </LocalizedLink>
      </div>

      {/* Navigation */}
      <nav className="flex-1 flex flex-col px-[8px]">
        {navItems.filter((item) => tier !== 'student' || item.href !== '/builder').map((item) => {
          const isActive = currentPath === item.href ||
            (item.href !== '/dashboard' && currentPath.startsWith(item.href));

          return (
            <LocalizedLink
              key={item.href}
              to={item.href}
              className={cn(
                "flex items-center gap-[8px] px-[16px] py-[12px] rounded-[8px] w-full text-left transition-colors",
                isActive
                  ? "bg-[var(--grey-accent,#eeeeee)]"
                  : "hover:bg-[var(--grey-accent,#eeeeee)]"
              )}
            >
              <item.icon
                className={cn(
                  isActive
                    ? "text-[color:var(--main-brand,#2197f1)]"
                    : "text-[color:var(--grey-text,#565858)]"
                )}
                size={20}
              />
              <span className={cn(
                "font-manrope font-semibold text-[14px] leading-[20px]",
                isActive
                  ? "text-[color:var(--main-brand,#2197f1)]"
                  : "text-[color:var(--grey-text,#565858)]"
              )}>
                {t(item.labelKey)}
              </span>
            </LocalizedLink>
          );
        })}
      </nav>

      {/* Bottom navigation */}
      <div className="mt-auto flex flex-col px-[8px] py-[16px] pb-[calc(16px+env(safe-area-inset-bottom,0px))]">
        {/* Feedback */}
        <button
          type="button"
          onClick={onOpenFeedback}
          className="flex items-center gap-[8px] px-[16px] py-[12px] rounded-[8px] w-full text-left transition-colors hover:bg-[var(--grey-accent,#eeeeee)]"
        >
          <FeedbackIcon
            className="text-[color:var(--grey-text,#565858)]"
            size={20}
          />
          <span className="font-manrope font-semibold text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">
            {t('sidebar.feedback')}
          </span>
        </button>

        {/* Help */}
        <LocalizedLink
          to="/help"
          className={cn(
            "flex items-center gap-[8px] px-[16px] py-[12px] rounded-[8px] w-full text-left transition-colors",
            currentPath === '/help'
              ? "bg-[var(--grey-accent,#eeeeee)]"
              : "hover:bg-[var(--grey-accent,#eeeeee)]"
          )}
        >
          <BookIcon
            className={cn(
              currentPath === '/help'
                ? "text-[color:var(--main-brand,#2197f1)]"
                : "text-[color:var(--grey-text,#565858)]"
            )}
            size={20}
          />
          <span className={cn(
            "font-manrope font-semibold text-[14px] leading-[20px]",
            currentPath === '/help'
              ? "text-[color:var(--main-brand,#2197f1)]"
              : "text-[color:var(--grey-text,#565858)]"
          )}>
            {t('sidebar.help')}
          </span>
        </LocalizedLink>

        {/* Subscription */}
        <LocalizedLink
          to="/subscription"
          className={cn(
            "flex items-center gap-[8px] px-[16px] py-[12px] rounded-[8px] w-full text-left transition-colors",
            currentPath === '/subscription'
              ? "bg-[var(--grey-accent,#eeeeee)]"
              : "hover:bg-[var(--grey-accent,#eeeeee)]"
          )}
        >
          <CardTickIcon
            className={cn(
              currentPath === '/subscription'
                ? "text-[color:var(--main-brand,#2197f1)]"
                : "text-[color:var(--grey-text,#565858)]"
            )}
            size={20}
          />
          <span className={cn(
            "font-manrope font-semibold text-[14px] leading-[20px]",
            currentPath === '/subscription'
              ? "text-[color:var(--main-brand,#2197f1)]"
              : "text-[color:var(--grey-text,#565858)]"
          )}>
            {t('sidebar.subscription')}
          </span>
          {tier !== 'free' && (
            <span className="font-segoe text-[10px] leading-[16px] text-[color:var(--main-brand,#2197f1)]">
              {tierLabels[tier]}
            </span>
          )}
        </LocalizedLink>

        {/* Language switcher */}
        <div className="px-[16px] py-[8px]">
          <LanguageSwitcher />
        </div>

        {/* User profile */}
        <LocalizedLink
          to="/profile"
          className={cn(
            "flex items-center gap-[8px] px-[16px] py-[12px] rounded-[8px] w-full text-left transition-colors",
            currentPath === '/profile'
              ? "bg-[var(--grey-accent,#eeeeee)]"
              : "hover:bg-[var(--grey-accent,#eeeeee)]"
          )}
        >
          <SettingIcon
            className={cn(
              currentPath === '/profile'
                ? "text-[color:var(--main-brand,#2197f1)]"
                : "text-[color:var(--grey-text,#565858)]"
            )}
            size={20}
          />
          <span className={cn(
            "font-manrope font-semibold text-[14px] leading-[20px]",
            currentPath === '/profile'
              ? "text-[color:var(--main-brand,#2197f1)]"
              : "text-[color:var(--grey-text,#565858)]"
          )}>
            {userName}
          </span>
        </LocalizedLink>
      </div>
    </aside>
    </>
  );
}
